import React, { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ModalProps } from '../../types';
import { acquireBodyScrollLock } from '../../utils/bodyScrollLock';

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className = '',
}) => {
  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const releaseScroll = acquireBodyScrollLock();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      releaseScroll();
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  const modalTree = (
    <div className="fixed inset-0 z-[200] overflow-hidden overscroll-none">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative flex h-full items-center justify-center p-4 py-8 sm:py-10">
        <div
          className={`relative flex max-h-[min(90dvh,calc(100vh-2rem))] w-full flex-col overflow-y-auto overscroll-contain rounded-lg bg-white shadow-soft-lg ${sizeClasses[size]} ${className}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
        >
          {/* Header */}
          {title && (
            <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 p-5 sm:p-6">
              <h2
                id="modal-title"
                className="text-xl font-display font-semibold text-neutral-900"
              >
                {title}
              </h2>
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-600 transition-colors duration-normal"
                aria-label="Fermer"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          <div className="p-5 sm:p-6">{children}</div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalTree, document.body);
  }

  return modalTree;
};

export default Modal;
